// - Timestamp-based: no reliance on background timers firing on time
// - Reconcile on visibility/focus
// - Cross-tab activity sync: BroadcastChannel + localStorage fallback
// - Singleton guard: no duplicated listeners on remounts
// - Auth-aware: only active when verified user is logged in

import { onMounted } from 'vue'
import { auth } from '@/main'
import { signOut, onAuthStateChanged } from 'firebase/auth'
import router from '@/router'
import { RoutesEnum } from '@/enums'

// ---- config ----------------------------------------------------------------
const IDLE_TIMEOUT_MINUTES = parseInt(process.env.VUE_APP_IDLE_TIMEOUT_MINUTES || '10', 10)
const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000
const DEBUG = String(process.env.VUE_APP_IDLE_DEBUG || 'false') === 'true'
const PRE_LOG_WINDOW_MS = 10_000 // log "about to logout" when <= 10s remain

// Cross-tab sync
const CHANNEL_NAME = 'bfs-idle-activity'
const STORAGE_KEY = 'bfs:last-activity-ts'

// Module singletons to avoid duplicate listeners across multiple mounts
let singletonStarted = false
let unsubAuth = null
let bc = null

// State for idle detection
let isActive = false                 // auth gate: only track when true
let lastActivity = Date.now()        // ms timestamp of last activity
let checkTimer = null                // foreground re-check timer

// ---- helpers ---------------------------------------------------------------
const dlog = (...args) => { if (DEBUG) console.log('[useIdleTimeout]', ...args) }
const dwarn = (...args) => { if (DEBUG) console.warn('[useIdleTimeout]', ...args) }

function now() { return Date.now() }

function safeClearTimer() {
  if (checkTimer) {
    clearTimeout(checkTimer)
    checkTimer = null
    dlog('Cleared foreground timer')
  }
}

async function performLogout(reason = 'idle') {
  dwarn('Logging out due to idle:', reason)
  teardown()
  try {
    await signOut(auth)
  } catch (e) {
    console.error('[useIdleTimeout] signOut error:', e)
  } finally {
    router.push(RoutesEnum.LOGIN)
  }
}

// Broadcast activity to other tabs
function broadcastActivity(ts) {
  try {
    if (bc) {
      bc.postMessage({ type: 'activity', ts })
      dlog('BroadcastChannel -> activity sent:', new Date(ts).toISOString())
    }
  } catch (e) {
    console.error('[useIdleTimeout] BC postMessage error:', e)
  }

  // storage fallback (and for Safari quirks)
  try {
    localStorage.setItem(STORAGE_KEY, String(ts))
    dlog('localStorage -> activity set:', ts)
    // optional remove to ensure subsequent changes always trigger 'storage'
    setTimeout(() => {
      try {
        localStorage.removeItem(STORAGE_KEY)
        dlog('localStorage -> activity removed (trigger event reliability)')
      } catch (e) {
        console.error('[useIdleTimeout] localStorage remove error:', e)
      }
    }, 0)
  } catch (e) {
    console.error('[useIdleTimeout] localStorage set error:', e)
  }
}

// Update activity timestamp and reschedule if visible
function markActivity(src = 'event') {
  if (!isActive) return
  lastActivity = now()
  dlog(`Activity (${src}) at`, new Date(lastActivity).toISOString())
  broadcastActivity(lastActivity)
  scheduleForegroundCheck()
}

// Foreground check: only runs while visible.
// In background, we don't schedule ticking; we reconcile on resume.
function checkDeadline() {
  if (!isActive) return
  const elapsed = now() - lastActivity
  const remaining = IDLE_TIMEOUT_MS - elapsed

  if (remaining > 0 && remaining <= PRE_LOG_WINDOW_MS) {
    dlog(`⚠️ Idle timeout in ${Math.ceil(remaining / 1000)}s`)
  }

  if (remaining <= 0) {
    performLogout('idle-deadline')
    return
  }

  // schedule next fire to when the deadline hits (clamped to min 1s)
  if (document.visibilityState === 'visible') {
    safeClearTimer()
    const next = Math.max(remaining, 1000)
    dlog('Scheduling next foreground check in ms:', next)
    checkTimer = setTimeout(checkDeadline, next)
  } else {
    // In background: don't keep timers; reconcile when visible again
    dlog('Tab not visible; skipping timer until visibility/focus')
    safeClearTimer()
  }
}

function scheduleForegroundCheck() {
  safeClearTimer()
  if (!isActive) return
  if (document.visibilityState !== 'visible') {
    dlog('Not scheduling check (tab hidden)')
    return
  }
  const remaining = Math.max(IDLE_TIMEOUT_MS - (now() - lastActivity), 0)
  dlog('Scheduling foreground check; remaining(ms)=', remaining)
  checkTimer = setTimeout(checkDeadline, Math.max(remaining, 1000))
}

// When tab becomes visible or gains focus, reconcile immediately
function onVisibilityOrFocus() {
  if (!isActive) return
  const elapsed = now() - lastActivity
  dlog(
    `Visibility/focus — elapsed ${Math.round(elapsed / 1000)}s since last activity; ` +
    `timeout=${IDLE_TIMEOUT_MINUTES}m`
  )
  if (elapsed >= IDLE_TIMEOUT_MS) {
    performLogout('idle-visible')
    return
  }
  scheduleForegroundCheck()
}

// ---- listeners -------------------------------------------------------------
const activityEvents = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
]

function addActivityListeners() {
  activityEvents.forEach((ev) =>
    window.addEventListener(ev, () => markActivity(ev), { passive: true })
  )
  document.addEventListener('visibilitychange', onVisibilityOrFocus)
  window.addEventListener('focus', onVisibilityOrFocus)
  dlog('Activity listeners attached')
}

function removeActivityListeners() {
  activityEvents.forEach((ev) => window.removeEventListener(ev, () => markActivity(ev)))
  document.removeEventListener('visibilitychange', onVisibilityOrFocus)
  window.removeEventListener('focus', onVisibilityOrFocus)
  dlog('Activity listeners removed')
}

// ---- cross-tab sync setup --------------------------------------------------
function setupBroadcast() {
  // Try BroadcastChannel first
  try {
    bc = new BroadcastChannel(CHANNEL_NAME)
    bc.onmessage = (ev) => {
      const ts = ev?.data?.ts
      if (typeof ts === 'number') {
        dlog('BC <- activity received:', new Date(ts).toISOString())
      }
      if (typeof ts === 'number' && ts > lastActivity) {
        lastActivity = ts
        scheduleForegroundCheck()
      }
    }
    dlog('BroadcastChannel ready:', CHANNEL_NAME)
  } catch (e) {
    bc = null
    dlog('BroadcastChannel not available; using storage fallback only')
  }

  // storage fallback (also used alongside BC for wider coverage)
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      const ts = Number(e.newValue)
      dlog('storage <- activity received:', ts)
      if (!Number.isNaN(ts) && ts > lastActivity) {
        lastActivity = ts
        scheduleForegroundCheck()
      }
    }
  })
}

// On startup, try to hydrate lastActivity from storage (soft best-effort)
function hydrateLastActivityFromStorage() {
  try {
    const val = localStorage.getItem(STORAGE_KEY)
    const ts = val ? Number(val) : NaN
    if (!Number.isNaN(ts) && ts > 0) {
      lastActivity = ts
      dlog('Hydrated lastActivity from storage:', new Date(ts).toISOString())
    } else {
      dlog('No stored lastActivity to hydrate')
    }
  } catch (e) {
    console.error('[useIdleTimeout] hydrate error:', e)
  }
}

// ---- lifecycle -------------------------------------------------------------
function setup() {
  if (singletonStarted) {
    dlog('setup() skipped (singleton already started)')
    return
  }
  singletonStarted = true
  dlog('setup() begin')

  hydrateLastActivityFromStorage()
  setupBroadcast()
  addActivityListeners()

  // Initial schedule for foreground
  scheduleForegroundCheck()

  // Subscribe to auth state last (so listeners are ready)
  unsubAuth = onAuthStateChanged(auth, (user) => {
    const verified = Boolean(user?.emailVerified)
    dlog('Auth state changed; verified=', verified)
    if (verified && !isActive) {
      isActive = true
      // Mark immediate activity to start a fresh window
      markActivity('auth-verified')
    } else if (!verified && isActive) {
      isActive = false
      // If user becomes unverified/unauthenticated, stop everything
      performLogout('auth-state-change')
    }
  })
}

function teardown() {
  dlog('teardown() begin')
  safeClearTimer()
  removeActivityListeners()
  try {
    if (bc && bc.close) {
      bc.close()
      dlog('BroadcastChannel closed')
    }
  } catch (e) {
    console.error('[useIdleTimeout] BC close error:', e)
  }
  bc = null

  if (unsubAuth) {
    try { unsubAuth() } catch (e) { console.error('[useIdleTimeout] unsubAuth error:', e) }
    unsubAuth = null
    dlog('Auth subscription removed')
  }

  singletonStarted = false
  dlog('teardown() end')
}

// ---- public composable -----------------------------------------------------
export function useIdleTimeout() {
  onMounted(() => {
    setup()
  })
}
